import axios from "axios";
import fileDownload from "js-file-download";
import moment from "moment";
import { useState } from "react";
import { GetApp, SkipNext, SkipPrevious } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import ActionButton from "../../Components/ActionButton";
import Gauge from "../../Components/Gauge";
import Tile from "../../Components/Tiles/Tile";
import TileActions from "../../Components/Tiles/TileActions";
import TileContent from "../../Components/Tiles/TileContent";
import { Return, CompoundOption } from "../../system/Domain";
import useAsyncEffect from "../../system/useAsyncEffect";
import compoundIdsQuery from "./compoundIdsQuery";

interface CompoundNewOrdersTileProps {
	compounds: CompoundOption[];
}

// Compounds have no notion of a fixed capacity yet, so the gauge's upper bound is derived from
// the current value instead of a hardcoded (Dorfmark-specific) constant.
const gaugeMax = (value: number) => Math.max(Math.ceil(value * 1.3), 20);

const multipleCompoundsTooltip = "Nur bei Auswahl eines einzelnen Platzes verfügbar";

export default ({ compounds }: CompoundNewOrdersTileProps) => {
	const [date, setDate] = useState(moment());
	const [data, setData] = useState<Return>({ morning: 0, afternoon: 0 });
	const [downloading, setDownloading] = useState<boolean>(false);
	const compoundIds = compounds.map((c) => c.id);
	const singleCompound = compounds.length === 1 ? compounds[0] : undefined;
	// Join into a primitive so the effect only re-runs when the actual ids change, not on every
	// render (compoundIds is a new array reference every render since it's derived via .map()).
	const compoundIdsKey = compoundIds.join(",");

	useAsyncEffect(async () => {
		if (compoundIds.length === 0) {
			setData({ morning: 0, afternoon: 0 });
			return;
		}

		const response = await axios.get<Return>(
			`/api/compounds/kpis/returns/${date.format("YYYY-MM-DD")}?${compoundIdsQuery(compoundIds)}`
		);
		setData(response.data);
	}, [date, compoundIdsKey]);

	const handleNextDay = () => {
		const newDate = date.clone().add(1, "day");
		setDate(newDate);
	};

	const handlePreviousDay = () => {
		const newDate = date.clone().add(-1, "day");
		setDate(newDate);
	};

	const handleDownload = async () => {
		if (!singleCompound) {
			return;
		}

		try {
			setDownloading(true);
			const response = await axios.get(
				`/api/returns/${date.format("YYYY-MM-DD")}/meldeliste?compound=${encodeURIComponent(singleCompound.name)}`
			);
			fileDownload(response.data, `Meldeliste-${date.format("DD-MM-YYYY")}.csv`, "text/csv", "\uFEFF");
		} finally {
			setDownloading(false);
		}
	};

	const handleDownloadQrCodes = async () => {
		if (!singleCompound) {
			return;
		}

		try {
			setDownloading(true);

			const response = await axios.get(
				`/api/orders/transports/planned-returns/qr-code?date=${date.format("YYYY-MM-DD")}`,
				{
					responseType: "blob"
				}
			);

			fileDownload(response.data, `${date.format("YYYY-MM-DD")} QrCodes.pdf`);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Tile sxProp={{ width: 640 }} title={`Auftragseingang ${date.format("L")}`}>
			<TileContent>
				<Gauge value={data.morning} max={gaugeMax(data.morning)} title="Vormittags" />
				<Gauge value={data.afternoon} max={gaugeMax(data.afternoon)} title="Nachmittags" />
			</TileContent>
			<TileActions>
				<div>
					<Tooltip title={singleCompound ? "" : multipleCompoundsTooltip}>
						<Box component="span" mr={2}>
							<ActionButton
								variant="text"
								disabled={!singleCompound || downloading}
								onClick={handleDownloadQrCodes}
							>
								Qr Codes
							</ActionButton>
						</Box>
					</Tooltip>
					<Tooltip title={singleCompound ? "" : multipleCompoundsTooltip}>
						<Box component="span" mr={2}>
							<IconButton size="small" disabled={!singleCompound || downloading} onClick={handleDownload}>
								<GetApp />
							</IconButton>
						</Box>
					</Tooltip>
					<IconButton size="small" onClick={handlePreviousDay}>
						<SkipPrevious />
					</IconButton>
					<IconButton size="small" onClick={handleNextDay}>
						<SkipNext />
					</IconButton>
				</div>
			</TileActions>
		</Tile>
	);
};
